import { inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { filter, map } from 'rxjs/operators';
import { DebouncedEventTracker } from '../../../cross-team-shared/event-tracking/debounced-event-tracker';
import { buildTrackerFromShellConnector } from '../../../cross-team-shared/event-tracking/event-utils';
import { KeyboardShortcutService } from '../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { EditMode3D } from '../../../cross-team-shared/types/tool-modes';
import { EventTrackingValue } from '../../../team-apis/core/event-tracking';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { getShapeForEventTracking } from '../../utils/misc-utils';
import { hasMainRendererLayer } from '../../utils/shape-visibility-utils';
import { CuboidHelper } from '../mesh/cuboid/cuboid-helper';
import { CuboidService } from '../mesh/cuboid/cuboid.service';
import { TransformShortcut } from './transform-shortcuts';

@Injectable()
export abstract class AbstractShortcutTransformService extends InjectableSuperclass {
  protected store = inject(PointCloudContentStore);

  private transformEventTracker: DebouncedEventTracker;
  private cuboidService = inject(CuboidService);
  private keyboardShortcutService = inject(KeyboardShortcutService);
  private shellConnector = inject(shellConnectorToken);

  constructor(private transformShortcuts: TransformShortcut[]) {
    super();

    this.transformEventTracker = new DebouncedEventTracker(
      { id: 'edit_shape_points', bucket: 'adjustment' },
      buildTrackerFromShellConnector(this.shellConnector),
      (event, option) => this.shellConnector.pushAndTrack(event, option),
    );
    this.transformEventTracker.addContext({
      batch_included_small_nudge: false,
    });

    this.setUpSubscriptions();
  }

  protected trackTransform(
    focusedCuboid: CuboidHelper,
    shortcut: string,
    method: EditMode3D,
    eventTrackingValue: EventTrackingValue,
  ) {
    if (shouldDoSmallTransform(shortcut)) {
      this.transformEventTracker.addBatchContext({
        batch_included_small_nudge: true,
      });
    }

    const cuboidState = this.store.state().render.cuboids[focusedCuboid.index];
    this.transformEventTracker.trackWithDebounce({
      ...eventTrackingValue,
      method:
        eventTrackingValue.outputType === 'VideoOutput'
          ? `${method}_3D`
          : method,
      shapes: [getShapeForEventTracking(cuboidState)],
    });
  }

  private buildTransformShortcutSubscription$(shortcut: string) {
    return this.keyboardShortcutService.getMatchingEvent$(shortcut).pipe(
      map(({ keyboardEvent, eventTrackingValue }) => {
        keyboardEvent.preventDefault(); // prevent browser scroll
        const focusedCuboid = this.store.getFocusedShapeStore()?.state();
        if (!focusedCuboid) {
          return undefined;
        }

        if (hasMainRendererLayer(focusedCuboid, this.store.state())) {
          return {
            cuboid: this.cuboidService.getHelper(focusedCuboid.index),
            eventTrackingValue,
          };
        }
        return undefined;
      }),
      filter(isDefined),
    );
  }

  private setUpSubscriptions() {
    this.transformShortcuts.forEach((shortcut) => {
      this.subscribeTo(
        this.buildTransformShortcutSubscription$(shortcut),
        ({ cuboid, eventTrackingValue }) => {
          this.transformCuboid(cuboid, shortcut, eventTrackingValue);
        },
      );
    });
  }

  protected abstract transformCuboid(
    cuboid: CuboidHelper,
    shortcut: string,
    eventTrackingValue: EventTrackingValue,
  ): void;
}

export function shouldDoSmallTransform(shortcut: string) {
  return shortcut.endsWith('Slow');
}

export function getShortcutNameWithoutTransformModifier(shortcut: string) {
  return shortcut.slice().replace('Slow', '');
}
